import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import DateFilter from "./DateFilter";
import DropdownFilterCat from "./DropdownFilterCat";
import DropdownFilterType from "./DropdownFilterType";


type DocumentItem = {
  id: number;
  name: string;
  path: string;
  pp: number;
  program: number;
  project: number;
  type: number;
  origin?: string;
  subcontractorId?: number | null;
  intervenants?: Array<{
    id: number;
  }>;
  at?: {
    date?: string;
  };
};

const FilterDocument = ({
  typesDocs,
  optionsType,
  dataDocuments,
  setDataFiltered,
  programsList,
  projectList
}: {
  typesDocs: any;
  optionsType: any;
  dataDocuments: any;
  setDataFiltered: any;
  programsList?: any[];
  projectList?: any[];
}) => {
  const navigate = useNavigate();
  const { idPp } = useParams();
  const [searchParams] = useSearchParams();

  const [filter, setFilter] = useState(() => ({
    search: "",
    categories: typesDocs.flatMap((o: any) => [
      o.value,
      ...(o.children?.map((c: any) => c.value) || []),
    ]) as number[],
    types: optionsType.map((o: any) => o.value) as string[],
    pps: [] as number[],
    programs: [] as number[],
    projects: [] as number[],
    intervenants: [] as number[],
    origin: null as string | null,
    date: null as string | null,
    after: null as string | null,
    before: null as string | null,
  }));

  const { t } = useTranslation("dashboard", {
    keyPrefix: "details",
  });

  const [searchValue, setSearchValue] = useState(filter.search);


  // 🔹 IDs de projets autorisés selon les programmes sélectionnés
  const validProjectIds = useMemo(() => {
    if (!filter.programs.length || !programsList?.length) return [];

    return programsList
      .filter((program: any) => filter.programs.includes(program.id))
      .flatMap((program: any) => program.projects || []);
  }, [filter.programs, programsList]);

  // 🔹 Mise à jour filtre + URL
  const handleChange = (slug: string, value: any[]) => {
    setFilter((prev: any) => {
      const updatedFilter: any = { ...prev };

      if (["categories", "types", "pps", "programs", "projects", "intervenants"].includes(slug)) {
        updatedFilter[slug] = value;
      } else {
        updatedFilter[slug] = value.length ? value[0] : null;
      }

      const params = new URLSearchParams();

      Object.entries(updatedFilter).forEach(([key, val]: any) => {
        if (
          val !== null &&
          val !== undefined &&
          val !== "" &&
          !(Array.isArray(val) && val.length === 0)
        ) {
          if (Array.isArray(val)) {
            params.set(key, val.join(","));
          } else {
            params.set(key, String(val));
          }
        }
      });

      navigate({ search: params.toString() });
      return updatedFilter;
    });
  };

  // 🔹 URL → State
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const updatedFilter: any = { ...filter };

    queryParams.forEach((value, key) => {
      if (key === "categories") {
        updatedFilter[key] = value.split(",").map(Number);
      } else if (key === "types") {
        updatedFilter[key] = value.split(",");
      } else if (key === "pps") {
        updatedFilter[key] = value.split(",").map(Number);
      } else if (key === "programs") {
        updatedFilter[key] = value.split(",").map(Number);
      } else if (key === "projects") {
        updatedFilter[key] = value.split(",").map(Number);
      } else if (key === "intervenants" || key === "subcontractors") {
        updatedFilter.intervenants = value.split(",").map(Number);
      } else if (key === "search") {
        updatedFilter[key] = value;
      } else if (key === "origin") {
        updatedFilter[key] = value;
      } else if (["after", "before", "date"].includes(key)) {
        updatedFilter[key] = value === "null" ? null : value;
      }
    });

    setFilter(updatedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // 🔹 Nettoyer les projets sélectionnés invalides
  useEffect(() => {
    if (!filter.projects.length || !filter.programs.length) return;

    const filteredSelectedProjects = filter.projects.filter((id) =>
      validProjectIds.includes(id),
    );

    if (filteredSelectedProjects.length !== filter.projects.length) {
      handleChange("projects", filteredSelectedProjects);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validProjectIds]);

  const extensionGroups: Record<string, string[]> = {
    document: ["doc", "docx", "txt", "odt", "rtf"],
    spreadsheet: ["xls", "xlsx", "ods", "csv"],
    image: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
    pdf: ["pdf"],
  };

  const resolveFileGroup = (fileName: string) => {
    const normalizedName = (fileName || "").trim().toLowerCase();
    const hasExtension = normalizedName.includes(".");
    const extension = hasExtension
      ? normalizedName.split(".").pop()?.toLowerCase() || ""
      : "";

    if (!hasExtension && normalizedName.startsWith("blob")) {
      return "image";
    }

    for (const [group, extensions] of Object.entries(extensionGroups)) {
      if (extensions.includes(extension)) {
        return group;
      }
    }

    return "";
  };
  const allCategoryValues = useMemo(
    () =>
      typesDocs.flatMap((item: any) => [
        Number(item.value),
        ...(item.children?.map((child: any) => Number(child.value)) || []),
      ]),
    [typesDocs],
  );

  const selectedCategoryLabels = useMemo(() => {
    const labels: string[] = [];

    typesDocs.forEach((parent: any) => {
      if (filter.categories.includes(Number(parent.value))) {
        labels.push(parent.label);
        return;
      }

      parent.children?.forEach((child: any) => {
        if (filter.categories.includes(Number(child.value))) {
          labels.push(child.label);
        }
      });
    });

    return labels;
  }, [filter.categories, typesDocs]);

  const showSelectedCategories =
    filter.categories.length > 0 &&
    filter.categories.length < allCategoryValues.length;
  // 🔹 Filtrage docs
  useEffect(() => {
    if (!dataDocuments) return;

    const allDocuments: DocumentItem[] = Object.values(dataDocuments).flat();
    const datasFiltered = allDocuments.filter((pj) => {
      const groupFound = resolveFileGroup(pj.name);

      const matchesSearch =
        !filter.search ||
        pj.name.toLowerCase().includes(filter.search.toLowerCase());

      const matchesTypes =
        !filter.types.length || filter.types.includes(groupFound);

      const matchesIdPp = !idPp || pj.pp === Number(idPp);

      const matchesProgram =
        !filter.programs.length || filter.programs.includes(pj.program);

      const matchesPp =
        !filter.pps.length || filter.pps.includes(Number(pj.pp));

      const matchesProject =
        !filter.projects.length || filter.projects.includes(pj.project);

      const documentIntervenantIds = Array.isArray(pj.intervenants)
        ? pj.intervenants
            .map((intervenant) => Number(intervenant?.id))
            .filter((id) => Number.isFinite(id) && id > 0)
        : [];

      const matchesIntervenant =
        !filter.intervenants.length ||
        filter.intervenants.some((id) => documentIntervenantIds.includes(id));

      const matchesOrigin =
        !filter.origin || pj.origin === filter.origin;

      const matchesDocType =
        !filter.categories.length ||
        filter.categories.includes(Number(pj.type));

      let matchesDate = true;
      const docDate = pj.at?.date ? new Date(pj.at.date) : null;

      if (filter.after || filter.before) {
        if (docDate) {
          if (filter.after) {
            matchesDate = matchesDate && docDate >= new Date(filter.after);
          }
          if (filter.before) {
            matchesDate = matchesDate && docDate <= new Date(filter.before);
          }
        } else {
          matchesDate = false;
        }
      }

      if (filter.date && docDate) {
        const now = new Date();

        switch (filter.date) {
          case "today":
            matchesDate = docDate.toDateString() === now.toDateString();
            break;

          case "lastWeek": {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(now.getDate() - 7);
            matchesDate = docDate >= sevenDaysAgo && docDate <= now;
            break;
          }

          case "lastMonth": {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(now.getDate() - 30);
            matchesDate = docDate >= thirtyDaysAgo && docDate <= now;
            break;
          }

          case "thisYear":
            matchesDate = docDate.getFullYear() === now.getFullYear();
            break;

          case "lastYear":
            matchesDate = docDate.getFullYear() === now.getFullYear() - 1;
            break;

          default:
            break;
        }
      }

      return (
        matchesSearch &&
        matchesTypes &&
        matchesIdPp &&
        matchesPp &&
        matchesProgram &&
        matchesProject &&
        matchesIntervenant &&
        matchesOrigin &&
        matchesDocType &&
        matchesDate
      );
    });
    setDataFiltered(datasFiltered);
  }, [filter, dataDocuments, idPp, setDataFiltered]);

  return (
    <div className="relative">
      <div className="mb-8">
        <div className="md:w-1/4">
          <div className="relative">
            <Input
              type="search"
              placeholder="Rechercher un fichier"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={(e) =>
                handleChange("search", e.target.value ? [e.target.value] : [])
              }
              className="h-11 text-[14px]"
            />
            <Search
              size={16}
              className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
            />
          </div>
        </div>

        <div className="flex justify-between flex-wrap items-start mt-3">
          <div className="md:w-2/3 flex gap-x-3 gap-y-2 items-center flex-wrap md:mb-0 mb-5">
            <DropdownFilterCat
              options={typesDocs}
              name="Categories"
              slug="categories"
              value={filter.categories}
              handleChange={handleChange}
            />

            <DropdownFilterType
              options={optionsType}
              name="Types"
              slug="types"
              value={filter.types}
              handleChange={handleChange}
            />

            <DateFilter
              handleChange={handleChange}
              initialAfter={filter.after}
              initialBefore={filter.before}
            />

            {/* PROGRAMMES */}
            {programsList && programsList.length > 0 && (
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-xs py-[3px] h-[26px] border rounded-md px-3 font-normal flex gap-1 items-center">
                      {t("Programmes")}
                      {filter.programs.length > 0 && (
                        <span className="ml-1 text-[10px] opacity-60">
                          ({filter.programs.length})
                        </span>
                      )}
                      <ChevronDown size={16} />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    className="max-h-64 overflow-auto"
                  >
                    <DropdownMenuLabel>
                      <div className="flex gap-1 items-center">
                        <Checkbox
                          id="allprograms"
                          checked={filter.programs.length === 0}
                          onCheckedChange={() => handleChange("programs", [])}
                        />
                        <label htmlFor="allprograms">{t("Programmes")}</label>
                      </div>
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator />

                    {programsList.map(
                      (option: any, i: number) =>
                        option.name && (
                          <DropdownMenuCheckboxItem
                            key={i}
                            checked={filter.programs.includes(option.id)}
                            onSelect={(e) => e.preventDefault()}
                            onCheckedChange={(checked) => {
                              let updated = [...filter.programs];

                              if (checked) {
                                updated.push(option.id);
                              } else {
                                updated = updated.filter(
                                  (id) => id !== option.id,
                                );
                              }

                              handleChange("programs", updated);
                            }}
                            className="flex items-center gap-2 pl-2"
                          >
                            <Checkbox
                              checked={filter.programs.includes(option.id)}
                              className="pointer-events-none"
                            />
                            <span>{option.name}</span>
                          </DropdownMenuCheckboxItem>
                        ),
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* PROJETS */}
            {projectList && projectList.length > 0 && (
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-xs py-[3px] h-[26px] border rounded-md px-3 font-normal flex gap-1 items-center">
                      {t("Projets")}
                      {filter.projects.length > 0 && (
                        <span className="ml-1 text-[10px] opacity-60">
                          ({filter.projects.length})
                        </span>
                      )}
                      <ChevronDown size={16} />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    className="max-h-64 overflow-auto"
                  >
                    <DropdownMenuLabel>
                      <div className="flex gap-1 items-center">
                        <Checkbox
                          id="allprojects"
                          checked={filter.projects.length === 0}
                          onCheckedChange={() => handleChange("projects", [])}
                        />
                        <label htmlFor="allprojects">{t("Projets")}</label>
                      </div>
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator />

                    {projectList
                      .filter((project: any) => {
                        if (filter.programs.length === 0) return true;
                        return validProjectIds.includes(project.id);
                      })
                      .map(
                        (option: any, i: number) =>
                          option.name && (
                            <DropdownMenuCheckboxItem
                              key={i}
                              checked={filter.projects.includes(option.id)}
                              onSelect={(e) => e.preventDefault()}
                              onCheckedChange={(checked) => {
                                let updated = [...filter.projects];

                                if (checked) {
                                  updated.push(option.id);
                                } else {
                                  updated = updated.filter(
                                    (id) => id !== option.id,
                                  );
                                }

                                handleChange("projects", updated);
                              }}
                              className="flex items-center gap-2 pl-2"
                            >
                              <Checkbox
                                checked={filter.projects.includes(option.id)}
                                className="pointer-events-none"
                              />
                              <span>{option.name}</span>
                            </DropdownMenuCheckboxItem>
                          ),
                      )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </div>
      {showSelectedCategories && selectedCategoryLabels.length > 0 && (
        <div className="my-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            Catégories prises en compte :
          </span>
          {selectedCategoryLabels.map((label) => (
            <span
              key={label}
              className="rounded-full bg-muted px-2 py-1 text-xs text-foreground"
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterDocument;
